import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FileIcon as FilePdf } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress
} from '@mui/material';
import ReactJson from 'react-json-view';
import fetchData from '../../utils/fetchData';
import DOMPurify from 'dompurify'; // For safe HTML rendering
import CloseIcon from '@mui/icons-material/Close';
import { Visibility } from '@mui/icons-material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PdfJsonViewer from '../PDFViewer/PDFViewerNew';
import { Document, Page, pdfjs } from 'react-pdf';

// PDF.js worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export function DocumentsSection () {
  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0); // New state for current document

  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [openJsonDialog, setOpenJsonDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentPdfLink, setCurrentPdfLink] = useState('');
  const [pdfName, setPdfName] = useState('');
  const [attachmentJsonData, setAttachmentJsonData] = useState({});
  const [warningMessage, setWarningMessage] = useState('');

  // Add state for react-pdf
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (selectedApplicationData?.application_id) {
      setLoading(true);
      fetchData(
        `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/attachments?application_id=${selectedApplicationData.application_id}`,
        'GET'
      )
        .then(data => {
          setAttachments(data);
          setSelectedIndex(0); // reset to first document when new data arrives
        })
        .catch(error => console.error('Error fetching attachments:', error))
        .finally(() => setLoading(false));
    } else {
      setAttachments([]);
    }
  }, [selectedApplicationData]);

  const handleViewPDF = (link, name) => {
    setCurrentPdfLink(link);
    setPdfName(name);
    setAttachmentJsonData({});
    setOpenPdfDialog(true);
  };

  // Note: JSON viewing functionality is currently disabled in the UI
  /*
  const handleViewJSON = (docId, name) => {
    setPdfName(name);
    fetchData(
      `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/detailed_json?doc_id=${docId}`,
      'GET'
    )
      .then(data => {
        setAttachmentJsonData(data?.[0]?.json || data);
        setOpenJsonDialog(true);
      })
      .catch(error => console.error('Error fetching JSON:', error));
  };
  */

  const handleEditButton = () => {
    setOpenEditDialog(true);
    setOpenPdfDialog(false);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
  };

  const closeDialog = () => {
    setOpenPdfDialog(false);
    setOpenEditDialog(false);
    setOpenJsonDialog(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}
      >
        <CircularProgress sx={{ color: '#ea580c' }} />
      </Box>
    );
  }

  // Use the currently selected attachment or an empty object
  const selectedAttachment = attachments[selectedIndex] || {};

  return (
    <>
      <Card className='p-5 bg-white shadow-md'>
        <div className='flex justify-between items-start mb-4'>
          <h3 className='text-base font-semibold flex items-center'>
            <FilePdf className='h-4 w-4 mr-2 text-[#FF5533]' />
            Application Documents
          </h3>
          {/* <div className='flex gap-2'>
            <Button variant='outline' size='sm'>
              <Filter className='h-4 w-4 mr-2' />
              Filter
            </Button>
            <Button variant='outline' size='sm'>
              <Download className='h-4 w-4 mr-2' />
              Export All
            </Button>
          </div> */}
        </div>
        <div className='grid grid-cols-3 gap-6'>
          {/* Left Container: Selected Document Summary */}
          <div className='col-span-2'>
            {selectedAttachment && (
              <div className='bg-gray-100 rounded-md relative'>
                {/* Sticky header */}
                <div className='sticky top-0 z-10 bg-gray-50 p-4 border-b border-gray-200 rounded-t-md'>
                  <div className='flex items-center justify-between'>
                    <h4 className='font-semibold'>
                      {selectedAttachment.name}
                    </h4>
                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        className='px-3 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 !h-7'
                        onClick={() =>
                          handleViewPDF(
                            selectedAttachment.link,
                            selectedAttachment.name
                          )
                        }
                      >
                        <FilePdf className='h-3.5 w-3.5 mr-1.5' />
                        View PDF
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Scrollable content */}
                <div className='p-4 max-h-[300px] overflow-y-auto'>
                  <div
                    className=' text-gray-700'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        selectedAttachment.summary || ''
                      )
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {/* Right Container: Document List */}
          <div>
            <Card className='p-4'>
              <div className='flex justify-between items-center mb-3'>
                <h3 className=' font-semibold'>Document List</h3>
                {/* <Button variant='ghost' size='sm' className='h-8 w-8 p-0 px-0'>
                          <Filter className='h-4 w-4' />
                        </Button> */}
              </div>
              <div className='space-y-2 max-h-[230px] overflow-y-auto'>
                {attachments.map((doc, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedIndex(index)}
                    className={`border rounded-md shadow-md cursor-pointer ${selectedIndex === index
                      ? 'bg-[#FFF0ED] border-[#FFDED6]'
                      : 'bg-white'
                      }`}
                  >
                    <div className='flex items-center justify-between p-2'>
                      <div className='flex items-center gap-2 overflow-hidden'>
                        <FilePdf className='h-4 w-4 text-[#FF5533] flex-shrink-0' />
                        <div className='overflow-hidden'>
                          <h4 className='text-sm font-medium truncate'>
                            {doc.name}
                          </h4>
                          <p className='text-sm text-gray-500'>
                            {doc.pages ? `${doc.pages} pages` : ''}
                          </p>
                        </div>
                        <div className='flex gap-1 flex-shrink-0'>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0 px-0'
                            onClick={() => handleViewPDF(doc.link, doc.name)}
                          >
                            <Visibility sx={{ fontSize: 16 }} />
                          </Button>
                          {/* <Button
                              variant='ghost'
                              size='sm'
                              className='h-6 w-6 p-0 px-0'
                              onClick={() => console.log(`Download ${doc.name}`)}
                              >
                              <Download className='h-4 w-4' />
                              </Button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={openPdfDialog}
        onClose={() => setOpenPdfDialog(false)}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            width: '900px', // Let content determine width
            maxWidth: '900px', // More compact maximum width
            maxHeight: '85vh',
            m: 0, // Remove default margin
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          style={{
            color: '#000000',
            padding: '12px 16px', // Reduced padding
            background: 'transparent',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 500,
            fontSize: '14px' // Smaller font size
          }}
        >
          <span style={{ fontWeight: 500, fontSize: '16px' }}>{pdfName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedAttachment.is_application && (
              <Button
                variant='outline'
                size='sm'
                onClick={handleEditButton}
                style={{
                  fontSize: '12px',
                  padding: '2px 8px',
                  height: '26px',
                  borderColor: 'rgba(0,0,0,0.1)'
                }}
              >
                <PictureAsPdfIcon
                  style={{ fontSize: '14px', marginRight: '4px' }}
                />
                Edit PDF
              </Button>
            )}
            <Button
              onClick={() => setOpenPdfDialog(false)}
              variant='ghost'
              size='sm'
              style={{ padding: '4px', minWidth: 'auto', lineHeight: 1 }}
            >
              <CloseIcon fontSize='small' />
            </Button>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Prevent content overflow causing dialog resize
            height: 'auto', // Let content determine height
            maxHeight: '85vh' // More compact max height
          }}
        >
          {/* Edit button moved to pagination section */}
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column', // Changed to column to stack PDF viewer and pagination
              background: '#f8fafc',
              borderRadius: 0,
              boxShadow: 'none',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Main content container with sidebar and PDF */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Loading overlay - shows when PDF is loading */}
              {!numPages && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.9)',
                    zIndex: 10,
                    backdropFilter: 'blur(3px)'
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                      color: '#ea580c',
                      marginBottom: 2
                    }}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      fontSize: '16px',
                      color: '#475569',
                      fontWeight: 500
                    }}
                  >
                    Loading your document...
                  </Box>
                </Box>
              )}
              {/* Sidebar Thumbnails */}
              <div
                style={{
                  width: 100,
                  minWidth: 100, // Reduced width for sidebar
                  background: '#f3f4f6',
                  borderRight: '1px solid #e5e7eb',
                  overflowY: 'auto',
                  padding: '8px 0',
                  height: '100%'
                }}
              >
                {numPages && (
                  <Document
                    file={{ url: currentPdfLink }}
                    loading={null}
                    onLoadSuccess={() => {}}
                  >
                    {Array.from(new Array(numPages), (el, idx) => (
                      <div
                        key={idx}
                        style={{
                          margin: '8px 0',
                          borderRadius: 6,
                          border:
                            pageNumber === idx + 1
                              ? '2px solid #ea580c'
                              : '2px solid transparent',
                          boxShadow:
                            pageNumber === idx + 1
                              ? '0 0 0 2px #fde68a'
                              : '0 1px 2px #e5e7eb',
                          cursor: 'pointer',
                          background:
                            pageNumber === idx + 1 ? '#fff7ed' : '#fff'
                        }}
                        onClick={() => setPageNumber(idx + 1)}
                      >
                        <Page
                          pageNumber={idx + 1}
                          width={90}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={null}
                        />
                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: 12,
                            color: pageNumber === idx + 1 ? '#ea580c' : '#888',
                            fontWeight: pageNumber === idx + 1 ? 600 : 400,
                            margin: '2px 0 4px 0'
                          }}
                        >
                          {idx + 1}
                        </div>
                      </div>
                    ))}
                  </Document>
                )}
              </div>
              {/* Main PDF Page */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'auto',
                  background: '#fff',
                  position: 'relative',
                  height: '100%',
                  width: '100%', // Ensure the container takes full width
                  minWidth: 0, // Prevent flex item from growing beyond available space
                  minHeight: '400px' // Ensure minimum height regardless of content
                }}
              >
                {/* Loading indicator */}
                {!numPages && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: 'rgba(255,255,255,0.9)',
                      zIndex: 10
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <CircularProgress
                        size={48}
                        thickness={4}
                        sx={{
                          color: '#ea580c',
                          animation: 'pulse 1.5s ease-in-out infinite'
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#475569',
                        fontWeight: 500
                      }}
                    >
                      Loading your document...
                    </p>
                    <style jsx>{`
                      @keyframes pulse {
                        0% {
                          transform: scale(0.95);
                          opacity: 0.7;
                        }
                        50% {
                          transform: scale(1.05);
                          opacity: 1;
                        }
                        100% {
                          transform: scale(0.95);
                          opacity: 0.7;
                        }
                      }
                    `}</style>
                  </div>
                )}

                {/* Remove top pagination controls - moved to bottom */}

                {/* PDF Document Viewer */}
                <div
                  style={{
                    position: 'relative',
                    flex: '1',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    overflowY: 'auto',
                    padding: '8px 0'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    {/* PDF Document */}
                    <Document
                      file={{ url: currentPdfLink }}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      className='pdf-doc-wrapper'
                      loading={null}
                      options={{
                        cMapUrl:
                          'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/',
                        cMapPacked: true,
                        standardFontDataUrl:
                          'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/',
                        useSystemFonts: true,
                        isEvalSupported: true,
                        useWorkerFetch: true
                      }}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={800}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={1.5}
                        loading={null}
                        canvasBackground='#ffffff'
                        className='pdf-page'
                        error={error => (
                          <div className='text-red-500'>
                            Error loading page: {error.message}
                          </div>
                        )}
                      />
                    </Document>
                    {/* Pagination moved to fixed position outside the scrollable area */}
                  </div>
                </div>
              </div>
            </Box>

            {/* Fixed Bottom Pagination Controls - Outside of the scrollable area */}
            {numPages > 0 && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 12px', // Reduced padding top/bottom
                  height: '36px', // Fixed compact height
                  minHeight: '36px',
                  borderTop: '1px solid rgba(0,0,0,0.08)',
                  background: '#fff',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.02)'
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  onClick={() => setPageNumber(p => Math.max(p - 1, 1))}
                  disabled={pageNumber <= 1}
                  sx={{
                    minWidth: '32px',
                    height: '28px',
                    padding: '0px 4px',
                    fontSize: '13px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#e5e7eb',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      backgroundColor: '#f9fafb',
                      color: '#d1d5db'
                    }
                  }}
                >
                  ←
                </Button>
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    margin: '0 8px',
                    lineHeight: '28px'
                  }}
                >
                  Page{' '}
                  <input
                    type='number'
                    min={1}
                    max={numPages || 1}
                    value={pageNumber}
                    onChange={e => {
                      let val = Number(e.target.value);
                      if (val > numPages) val = numPages;
                      if (val < 1) val = 1;
                      setPageNumber(val);
                    }}
                    style={{
                      width: 30,
                      height: '22px',
                      textAlign: 'center',
                      border: '1px solid #e5e7eb',
                      borderRadius: 3,
                      margin: '0 4px',
                      fontSize: 12,
                      padding: '0px 2px',
                      lineHeight: '22px'
                    }}
                  />{' '}
                  of {numPages || 1}
                </span>
                <Button
                  variant='contained'
                  size='small'
                  onClick={() =>
                    setPageNumber(p => Math.min(p + 1, numPages || 1))
                  }
                  disabled={pageNumber >= (numPages || 1)}
                  sx={{
                    minWidth: '32px',
                    height: '28px',
                    padding: '0px 4px',
                    fontSize: '13px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#e5e7eb',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      backgroundColor: '#f9fafb',
                      color: '#d1d5db'
                    }
                  }}
                >
                  →
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* JSON Viewer Dialog */}
      <Dialog
        open={openJsonDialog}
        onClose={() => setOpenJsonDialog(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          {pdfName} JSON Data
          <span
            onClick={() => setOpenJsonDialog(false)}
            style={{ cursor: 'pointer', float: 'right' }}
          >
            X
          </span>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: '65vh', overflowY: 'auto' }}>
            <ReactJson
              src={attachmentJsonData || {}}
              theme='monokai'
              style={{ fontSize: '14px' }}
              displayObjectSize={false}
              displayDataTypes={false}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit PDF Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleEditDialogClose}
        maxWidth='xl'
        fullWidth
        PaperProps={{
          sx: {
            border: '1px solid #ea580c',
            height: '95%',
            width: '80%',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle style={{ color: '#ea580c', paddingBottom: '10px' }}>
          {pdfName}
          <span
            onClick={handleEditDialogClose}
            className='close-icon'
            style={{ cursor: 'pointer', float: 'right' }}
          >
            <CloseIcon />
          </span>
        </DialogTitle>
        <DialogContent>
          {warningMessage ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: '#ea580c26',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              ></Box>
              <WarningAmberOutlinedIcon
                sx={{ color: '#ea580c', marginRight: '8px' }}
              />
              <p style={{ margin: 0, color: '#ea580c' }}>{warningMessage}</p>
            </>
          ) : (
            <Box
              sx={{
                mb: 1,
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                display: 'none',
                alignItems: 'center',
                padding: '6px'
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#0d47a1', marginRight: '8px' }} />
              <p style={{ margin: 0, color: '#0d47a1', fontSize: '14px' }}>
                Review the document carefully before editing. Click on yellow
                areas on the PDF to update data in the highlighted panel.
              </p>
            </Box>
          )}
          <Box sx={{ height: '65vh' }}>
            <PdfJsonViewer
              docId={selectedAttachment.doc_id}
              applicationId={selectedApplicationData?.application_id}
              currentPdfLink={currentPdfLink}
              closeDialog={closeDialog}
              setWarningMessage={setWarningMessage}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .pdf-page {
          canvas {
            max-width: 100%;
            height: auto !important;
            background-color: white !important;
            image-rendering: high-quality;
            -webkit-font-smoothing: antialiased;
          }
        }
        .pdf-doc-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
          background-color: white;
        }
      `}</style>
    </>
  );
}
